import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ArrowRight } from '@phosphor-icons/react';
import { API } from '../context/AuthContext';

/**
 * Post-purchase feedback prompt.
 *
 * Surfaces ~1.5s after the checkout-success toast lands. Three emoji
 * buttons + an optional one-line comment + a "skip" link. Submits to
 * /api/feedback/post-purchase, which dual-writes to Mongo and emails
 * the operator.
 *
 * Idempotent on the frontend: once a session_id has been submitted (or
 * dismissed) we mark it in localStorage so a refresh + re-open doesn't
 * re-prompt. localStorage is appropriate here — it's a UI-dismissal flag
 * with no security implications.
 *
 * Triggered by CheckoutReturnHandler immediately after the unlock toast.
 */
const STORAGE_PREFIX = 'defectr:fb:';

export default function PostPurchaseFeedback({ sessionId, onClose }) {
    const [step, setStep] = useState('react');   // react | comment | done
    const [reaction, setReaction] = useState(null);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Esc closes
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const markDone = () => {
        try { localStorage.setItem(STORAGE_PREFIX + sessionId, '1'); } catch { /* private mode */ }
    };

    const handleClose = () => {
        markDone();
        onClose?.();
    };

    const handleReact = (r) => {
        setReaction(r);
        setStep('comment');
    };

    const handleSubmit = async () => {
        if (submitting || !reaction) return;
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/feedback/post-purchase`,
                { session_id: sessionId, reaction, comment: comment.trim() || null },
                { withCredentials: true },
            );
            markDone();
            setStep('done');
            toast.success('Thanks for the feedback — really appreciate it.');
            // Auto-close after a beat so the user feels acknowledged but isn't blocked.
            setTimeout(() => onClose?.(), 1400);
        } catch (err) {
            console.error('feedback submit failed', err);
            toast.error('Couldn\'t submit just now — but it\'s not blocking anything.');
            // Don't markDone on failure so the user can retry on next paint.
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-6 sm:right-6 z-[60] p-4 sm:p-0 sm:max-w-sm pointer-events-none"
            data-testid="post-purchase-feedback"
        >
            <div className="bg-white border border-[#18181B] shadow-2xl pointer-events-auto animate-feedback-slide-in">
                <div className="flex items-center justify-between border-b border-[#E4E4E7] px-4 py-2.5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#52525B]">
                        Quick favour
                    </span>
                    <button
                        type="button"
                        onClick={handleClose}
                        data-testid="feedback-close-btn"
                        className="p-1 hover:bg-[#F4F4F5]"
                        aria-label="Dismiss"
                    >
                        <X size={16} />
                    </button>
                </div>

                {step === 'react' && (
                    <div className="p-4">
                        <p className="font-display font-semibold mb-3">How was that?</p>
                        <div className="grid grid-cols-3 gap-2" data-testid="feedback-reactions">
                            <ReactionButton emoji="😍" label="Loved it" testId="react-loved" onClick={() => handleReact('loved')} />
                            <ReactionButton emoji="😐" label="It's OK" testId="react-ok"    onClick={() => handleReact('ok')} />
                            <ReactionButton emoji="😬" label="Rough"   testId="react-rough" onClick={() => handleReact('rough')} />
                        </div>
                        <button
                            type="button"
                            onClick={handleClose}
                            data-testid="feedback-skip-btn"
                            className="mt-3 w-full text-[10px] font-mono uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-[#52525B] py-2"
                        >
                            Skip
                        </button>
                    </div>
                )}

                {step === 'comment' && (
                    <div className="p-4">
                        <p className="text-sm mb-1">
                            <span className="text-2xl mr-1.5">{({loved: '😍', ok: '😐', rough: '😬'})[reaction]}</span>
                            <span className="font-display font-semibold">Anything you&apos;d like us to know?</span>
                        </p>
                        <p className="text-xs text-[#52525B] mb-3">Optional — one sentence is plenty.</p>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value.slice(0, 500))}
                            data-testid="feedback-comment-input"
                            placeholder={reaction === 'rough'
                                ? 'What went wrong? It really helps us fix it.'
                                : reaction === 'loved'
                                    ? 'What worked best?'
                                    : 'What would have made it better?'}
                            rows={3}
                            className="w-full border border-[#E4E4E7] focus:border-[#18181B] outline-none px-3 py-2 text-sm resize-none"
                            autoFocus
                            disabled={submitting}
                        />
                        <div className="mt-3 flex items-center justify-between gap-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">
                                {comment.length}/500
                            </span>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitting}
                                data-testid="feedback-submit-btn"
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1FA5CF] text-white font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#1787AB] active:scale-95 transition-all duration-150 disabled:opacity-50"
                            >
                                Send
                                <ArrowRight size={12} weight="bold" />
                            </button>
                        </div>
                    </div>
                )}

                {step === 'done' && (
                    <div className="p-6 text-center" data-testid="feedback-thanks">
                        <div className="text-3xl mb-2">🙏</div>
                        <div className="font-display font-semibold">Thank you.</div>
                        <p className="text-xs text-[#52525B] mt-1">
                            Genuinely — this is how we make it better.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

function ReactionButton({ emoji, label, testId, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className="flex flex-col items-center gap-1 py-3 border border-[#E4E4E7] hover:border-[#18181B] active:scale-95 transition-all duration-150 group"
        >
            <span className="text-2xl group-hover:scale-110 transition-transform duration-200">{emoji}</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#52525B] group-hover:text-[#09090B]">
                {label}
            </span>
        </button>
    );
}

/** Small helper used by CheckoutReturnHandler to decide whether to mount
 *  the prompt at all. Stays a pure function so it's trivially testable. */
export function shouldShowFeedback(sessionId) {
    if (!sessionId) return false;
    try {
        return !localStorage.getItem(STORAGE_PREFIX + sessionId);
    } catch {
        return false;   // private mode → don't show, can't dedupe
    }
}
